import { useEffect, useRef, useState } from "react";
import { stockAPI } from "../../utlis/apiService";
import { notify } from "../../common/Toast";
import Loader from "../../common/Loader";
import Table from "../../common/Table";
import { RegistryHeader, CalculationFooter } from "../../common/Shared";

const BilletStock = () => {
  const [loading, setLoading] = useState(false);
  const [stockData, setStockData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const pageSize = 20;

  const isFetchingRef = useRef(false);
  const tableScrollRef = useRef(null);

  const fetchBilletStock = async () => {
    if (isFetchingRef.current) return;
    setLoading(true);
    try {
      const res = await stockAPI.getBilletStock(currentPage, pageSize);
      const items = res?.items || res?.data || res || [];

      if (currentPage === 1) {
        setStockData(items);
      } else {
        setStockData((prev) => [...prev, ...items]);
      }

      if (items.length < pageSize) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }
    } catch (err) {
      notify("Error fetching billet stock", "error");
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  };

  useEffect(() => {
    fetchBilletStock();
  }, [currentPage]);

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;

    const onScroll = () => {
      if (loading || !hasMore || isFetchingRef.current) return;

      const { scrollTop, clientHeight, scrollHeight } = el;

      if (scrollHeight - scrollTop <= clientHeight + 100) {
        isFetchingRef.current = true;
        setCurrentPage((p) => p + 1);
      }
    };

    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [loading, hasMore]);

  // Exclude technical/ID fields from UI Table
  const excludedKeys = [
    "id",
    "created_by",
    "createdBy",
    "updatedBy",
    "updated_by",
    "isDeleted",
    "deleted",
    "rowVersion",
    "concurrencyStamp",
    "billetStockId"
  ];

  // Map keys to Title Case or custom labels
  const formatHeader = (key) => {
    const overrides = {
      skuNo: "SKU No",
      sku_no: "SKU No",
      sku: "SKU",
      SKU: "SKU",
      materialName: "Material Name",
      material_name: "Material Name",
      alloyGrade: "Alloy Grade",
      alloy_grade: "Alloy Grade",
      grade: "Grade",
      billetSize: "Billet Size (mm)",
      billet_size: "Billet Size (mm)",
      length: "Length (mm)",
      noOfBillet: "No. of Billets",
      no_of_billet: "No. of Billets",
      quantity: "Quantity",
      qty: "Qty",
      weight: "Weight (Kg)",
      wt: "Weight (Kg)",
      totalWeight: "Total Weight (Kg)",
      total_weight: "Total Weight (Kg)",
      castingNo: "Casting No",
      casting_no: "Casting No",
      heatNo: "Heat No",
      heat_no: "Heat No",
      inwardNo: "Inward No",
      inward_no: "Inward No",
      createdDate: "Created Date",
      created_date: "Created Date",
    };

    if (overrides[key]) return overrides[key];

    return key
      .replace(/_/g, " ")
      .replace(/([A-Z])/g, " $1")
      .trim()
      .replace(/^\w/, (c) => c.toUpperCase());
  };

  // Generate table columns based on dynamic data keys
  const getColumns = () => {
    if (stockData.length === 0) return [];

    const firstItem = stockData[0];
    const keys = Object.keys(firstItem).filter((k) => !excludedKeys.includes(k));

    return keys.map((key) => {
      const isNumeric = typeof firstItem[key] === "number";
      return {
        key,
        label: formatHeader(key),
        align: isNumeric ? "right" : "center",
        render: (val) => {
          if (typeof val === "number") {
            return (
              <span className="font-bold">
                {val.toLocaleString(undefined, {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 2,
                })}
              </span>
            );
          }
          return val ?? "-";
        },
      };
    });
  };

  const columns = getColumns();

  const sumField = (items, possibleKeys) => {
    if (!items || items.length === 0) return 0;
    const firstItem = items[0];
    const key = Object.keys(firstItem).find(k =>
      possibleKeys.some(pk => pk.toLowerCase() === k.toLowerCase())
    );
    if (!key) return 0;
    return items.reduce((sum, item) => sum + (Number(item[key]) || 0), 0);
  };

  const totalWeight = sumField(stockData, ["totalWeight"]);
  const totalLogs = sumField(stockData, ["noOfLogs"]);

  return (
    <div className="flex flex-col h-full bg-white font-plus overflow-hidden relative">
      {loading && currentPage === 1 && <Loader centered />}

      <RegistryHeader title="Billet Stock Inventory" />

      <div
        ref={tableScrollRef}
        className="px-8 pt-0 pb-8 flex-1 overflow-auto relative"
      >
        {stockData.length > 0 ? (
          <div className="border border-gray-200 mt-4 overflow-hidden rounded-xl">
            <Table
              columns={columns}
              data={stockData}
              showActions={false}
              tableClassName="text-[12px] font-bold"
            />
            {loading && (
              <div className="py-4 text-center text-xs text-gray-500 font-bold uppercase tracking-wider bg-slate-50">
                Loading more stock...
              </div>
            )}
          </div>
        ) : (
          !loading && (
            <div className="bg-white py-32 text-center">
              <div className="text-gray-200 text-6xl mb-6">📦</div>
              <h3 className="text-[14px] font-black text-gray-400 uppercase tracking-[3px]">
                No Billet Stock Found
              </h3>
              <p className="text-[11px] text-gray-300 mt-2 font-bold uppercase tracking-widest">
                There is no billet stock currently registered in the system.
              </p>
            </div>
          )
        )}
      </div>

      {stockData.length > 0 && (
        <CalculationFooter
          items={[
            {
              label: "TOTAL LOGS",
              value: totalLogs.toLocaleString(undefined, { maximumFractionDigits: 0 }),
              suffix: "LOGS"
            },
            {
              label: "TOTAL WEIGHT",
              value: totalWeight.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
              suffix: "KG",
              color: "text-primary"
            }
          ]}
        />
      )}
    </div>
  );
};

export default BilletStock;
